import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import firebase from 'firebase/app';
import { UA, WebSocketInterface } from 'jssip';
import { Subject } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
    CallStatus,
    ProviderEndpoints,
    StunTurnConfiguration,
    Ticket,
    VoiceConnection,
    VoiceConnectionStatus,
    VoiceDevice,
    VoiceDeviceStatus,
} from '../../../models';
import { ParentVoiceService } from '../parent-voice.service';

@Injectable({
    providedIn: 'root',
})
export class JambonzVoiceService extends ParentVoiceService {
    JsSipSocket: WebSocketInterface;

    providerEndpoints: ProviderEndpoints = {
        TOKEN_URL: environment.jambonz.TOKEN_URL,
        OUTBOUND_REQUEST_URL: environment.jambonz.OUTBOUND_REQUEST_URL,
        CONFERENCE_HOLD_URL: environment.jambonz.CONFERENCE_HOLD_URL,
        CALL_TRANSFER_URL: environment.jambonz.CALL_TRANSFER_URL,
        CREATE_CALL_URL: environment.jambonz.CREATE_CALL_URL,
    };

    async setupDevice(idToken: string): Promise<VoiceDevice> {
        const socketAddress = environment.jambonz.socketAddress;
        const clientId = this.authentication.user.id;

        console.log('Phone init, connecting to ', socketAddress);

        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + idToken,
        });

        const authOptions = { headers };

        const stunTurnConfigurations =  await this.http.get<StunTurnConfiguration[]>(
              environment.jambonz.STUN_TURN_CONFIG,
              authOptions
          )
          .toPromise();

        this.JsSipSocket = new WebSocketInterface(socketAddress);

        const configuration = {
            sockets: [this.JsSipSocket],
            uri: environment.jambonz.UAConfigurationURI(clientId),
            password: btoa(idToken),
            register_expires: 54000
        };

        const device = new UA(configuration);

        device.on('registered', (e) => {
            console.log('registered');
            this.deviceStatusSubject.next({ status: VoiceDeviceStatus.Ready });
        });

        device.on('unregistered', async (e) => {
            console.log('unregistered');
            this.deviceStatusSubject.next({ status: VoiceDeviceStatus.Offline });

            const idToken = await this.getNewIdToken();
            const headers = new HttpHeaders({
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + idToken,
            });

            this.http.post(environment.jambonz.UNREGISTER_USER_URL, { clientId }, { headers }).toPromise();
        });

        const jambonzDevice = new JambonzDevice(device, this.http, clientId, this.deviceStatusSubject, this.connectionErrorSubject, stunTurnConfigurations);
        jambonzDevice.setup(idToken);
        return jambonzDevice;
    }

    shutDownDevice(): void {
        this.device.terminate();
    }

    hasValidToken(): boolean {
        return true;
    }
}

export class JambonzDevice implements VoiceDevice {
    deviceStatus: VoiceDeviceStatus = VoiceDeviceStatus.Offline;
    ticket: Ticket;

    constructor(
        private jssipUserAgent: UA,
        private http: HttpClient,
        private clientId: string,
        private deviceStatusSubject: Subject<CallStatus>,
        private connectionErrorSubject: Subject<{ ticket: Ticket }>,
        private stunTurnConfigurations: StunTurnConfiguration[]
    ) {
        deviceStatusSubject.subscribe((status) => (this.deviceStatus = status.status));
    }

    status(): VoiceDeviceStatus {
        return this.deviceStatus;
    }

    async connect(params?: any): Promise<VoiceConnection> {
        this.ticket = JSON.parse(params.ticket);
        // Register callbacks to desired call events
        const eventHandlers = {
            progress: (e: any) => {
                console.log(e);
                console.log('progress');
                this.deviceStatusSubject.next({
                    status: VoiceDeviceStatus.Busy,
                    ticketId: this.ticket.id,
                });
            },
            failed: (e: any) => {
                console.log('call failed with cause: ' + JSON.stringify(e));
                this.connectionErrorSubject.next({ ticket: this.ticket });
            },
            ended: (e: any) => {
                console.log(e);
                console.log('ended');
                this.deviceStatusSubject.next({
                    status: VoiceDeviceStatus.Ready,
                });
            },
            confirmed: (e: any) => {
                console.log(e);
                console.log('confirmed');
                this.deviceStatusSubject.next({
                    status: VoiceDeviceStatus.Busy,
                    ticketId: this.ticket.id,
                });
            },
            icecandidate: (e: any) => {
                console.log(e);
                console.log('iceCandidates');
                if (e?.candidate?.relatedAddress && e?.candidate?.relatedPort) {
                    e.ready();
                }              
          },            
        };

        console.log(this.stunTurnConfigurations)

        const options = {
            eventHandlers,
            mediaConstraints: { audio: true, video: false },
            pcConfig: {
              iceServers: this.stunTurnConfigurations
            }                        
        };

        const token = await firebase.firestore().app.auth().currentUser.getIdToken();

        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + token,
        });

        const authOptions = { headers };

        await this.http
            .post(
                environment.jambonz.REGISTER_CONFERENCE_URL,
                { clientId: this.clientId, conferenceId: params.conferenceInternalId },
                authOptions
            )
            .toPromise();

        const currentCall = this.jssipUserAgent.call(this.ticket?.channel?.id, options);
        if (currentCall) {
            currentCall.connection.addEventListener('addstream', (e: any) => {
                console.log('Audio listener');
                const audio = document.createElement('audio');
                audio.srcObject = e.stream;
                audio.play();
            });
        }
        return new JambonzConnection(currentCall);
    }

    setup(token: string, options?: any): any {
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + token,
        });

        const authOptions = { headers };

        this.http
            .post(environment.jambonz.REGISTER_USER_URL, { clientId: this.clientId }, authOptions)
            .toPromise()
            .then(() => {
                this.jssipUserAgent.start();
            });
    }

    terminate(): void {
        console.log('terminate');
        this.jssipUserAgent.stop();
        this.jssipUserAgent.unregister();
    }
}

interface RTCSession {
    status: number;
    isMuted(): { audio?: boolean; video?: boolean };
    terminate(): void;
    mute(): void;
    unmute(): void;
}

export class JambonzConnection implements VoiceConnection {
    constructor(private rtcSession: RTCSession) {}

    status(): VoiceConnectionStatus {
        switch (this.rtcSession.status) {
            //SessionStatus.STATUS_TERMINATED
            case 8:
            //SessionStatus.STATUS_NULL
            case 0:
            //SessionStatus.STATUS_CANCELED
            case 7:
                return VoiceConnectionStatus.Closed;
            //SessionStatus.STATUS_WAITING_FOR_ACK
            case 6:
            //SessionStatus.STATUS_WAITING_FOR_ANSWER
            case 4:
            //SessionStatus.STATUS_INVITE_SENT
            case 1:
                return VoiceConnectionStatus.Connecting;
            //SessionStatus.STATUS_CONFIRMED
            case 9:
                return VoiceConnectionStatus.Open;
        }
    }
    isMuted(): boolean {
        return this.rtcSession.isMuted().audio;
    }
    disconnect(): void {
        this.rtcSession.terminate();
    }
    mute(mute: boolean): void {
        mute ? this.rtcSession.mute() : this.rtcSession.unmute();
    }
}
